import type { UnitType } from '@packages/types';

// Pure helpers for ActivityWizardForm. No React, no IO.

// Sentinel for "no fee earner", the picker option for fees and disbursements.
// Member ids are UUIDs, so this string can never collide with a real member.
//
// InlineLineItemForm's invoice-line performer select (components/invoice/
// InlineLineItemForm.tsx) expresses the same concept differently: it uses a
// plain value="" and disambiguates "never touched" from "explicitly cleared"
// via `dirtyFields.performedByName` (invoice/lineEdit.helpers.ts). Both are
// valid ways to encode the distinction - dirtiness lives in form metadata a
// programmatic `setValue` bypasses unless it passes `{ shouldDirty: true }`,
// whereas this sentinel encodes the intent in the field's own value. This
// form sets the performer programmatically from two controls (the
// Disbursement checkbox and handleTypeSelect), so the value-based encoding
// is the more robust choice here. A non-empty sentinel also lets the blank
// first option stay a disabled placeholder rather than a selectable value.
export const UNASSIGNED_PERFORMER = 'unassigned';

export function isHoursType(unitType: UnitType | undefined): boolean {
  if (unitType === undefined) return false;
  return unitType === 'hours';
}

export function ratePlaceholder(unitType: UnitType | undefined): string {
  if (unitType === 'km') return '8.00';
  if (unitType === 'pages') return '5.00';
  return '1500.00';
}

/** Parses the rate field's decimal-rand string to minor units. Empty/invalid input is null. */
export function parseRateInputToMinorUnits(value: string): number | null {
  if (!value.trim()) return null;
  const parsed = parseFloat(value);
  if (Number.isNaN(parsed)) return null;
  return Math.round(parsed * 100);
}

/**
 * The file note as the backend stores it: whitespace-only is no note (null),
 * anything else is kept verbatim. Mirrors the backend's `normaliseActivityNote`,
 * so "did the note change" agrees with what a save would actually write.
 */
export function normaliseNote(note: string | null | undefined): string | null {
  if (note === undefined || note === null || note.trim().length === 0) return null;
  return note;
}

/** Whether two update payloads (built by the same builder) would write the same details. */
export function sameActivityDetails(a: object, b: object): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}
